import type { Dayjs } from 'dayjs';
import { type RRule, rrulestr } from 'rrule';

import type { Event, OptionsType, PartialUser, Recurrence } from '../../../Data/types';

// Strip HTML tags and decode entities
const stripHtmlTags = (html: string): string => {
  if (!html) return '';
  const div = document.createElement('div');
  div.innerHTML = html;
  return (div.textContent || '').replace(/\s+/g, ' ').trim();
};

const escapeCSV = (value: string) => {
  if (!value) return '';
  // Convert to string and remove any null bytes or control characters
  const cleanValue = String(value)
    .replace(/\0/g, '')
    // eslint-disable-next-line no-control-regex
    .replace(/[\x00-\x08\x0B\x0C\x0E-\x1F\x7F]/g, ''); // Remove control characters except \t, \n, \r
  // If value contains comma, quote, newline, or carriage return, wrap in quotes and escape quotes
  if (cleanValue.includes(',') || cleanValue.includes('"') || cleanValue.includes('\n') || cleanValue.includes('\r')) {
    return `"${cleanValue.replace(/"/g, '""')}"`;
  }
  return cleanValue;
};

const DATE_TIME_FORMAT = 'MM/DD/YYYYTHH:mm';

const formatDateTime = (value: Dayjs | null | undefined): string => {
  if (!value) return '';
  return value.format(DATE_TIME_FORMAT);
};

const parseCreatedAt = (createdAt: unknown): string => {
  if (!createdAt) return '';

  if ((createdAt as Dayjs).format) {
    return (createdAt as Dayjs).format(DATE_TIME_FORMAT);
  }

  return new Date(createdAt as string).toISOString();
};

const toEscapedCsv = (value: string): string => escapeCSV(value);

const joinOptionNames = (options: OptionsType[] | null | undefined): string => {
  if (!options || options.length === 0) return '';
  return options.map((option) => option?.name).join(',');
};

const joinUserField = (users: PartialUser[] | null | undefined, field: 'DisplayName' | 'WorkEmail'): string => {
  if (!users || users.length === 0) return '';
  return users.map((user) => user[field]).join(',');
};

const joinOutsideSpeakerNames = (speakers: Event['outsideSpeakers']): string => {
  if (!speakers || speakers.length === 0) return '';
  return speakers.map((speaker) => speaker.name).join(',');
};

const joinOfficeLocationNames = (officeLocations: Event['officeLocation']): string => {
  if (!officeLocations || officeLocations.length === 0) return '';

  return officeLocations
    .map((office) => office.name?.trim())
    .filter(Boolean)
    .join(', ');
};

const parseEventDateTime = (event: Event): string => {
  const start = event.isRecurring
    ? formatDateTime(event.recurrence?.startDate)
    : formatDateTime(event.eventDateTime?.start);
  const end = event.isRecurring ? formatDateTime(event.recurrence?.endDate) : formatDateTime(event.eventDateTime?.end);
  const separator = start && end ? '-' : '';

  return `${start}${separator}${end}`;
};

const parseStartOrEnd = (event: Event, key: 'start' | 'end'): string => {
  if (event.isRecurring) {
    return key === 'start' ? formatDateTime(event.recurrence?.startDate) : formatDateTime(event.recurrence?.endDate);
  }

  const date = (event as Record<string, unknown>)[key] as Dayjs | null | undefined;
  return formatDateTime(date);
};

const parseRecurrence = (recurrence: Recurrence | null | undefined): string => {
  const rrule = recurrence?.recurrencePattern ? (rrulestr(recurrence.recurrencePattern) as RRule) : null;
  const recurrencePattern = rrule ? rrule.toText() : '';
  const exceptions = recurrence?.exceptions?.map((exception) => exception.exceptionDate.format('YYYY-MM-DD')).join(',');

  if (!exceptions) return recurrencePattern;
  return `${recurrencePattern}, except: ${exceptions}`;
};

const parseDefaultField = (event: Event, key: string): string[] => {
  const val = (event as Record<string, unknown>)[key];

  if (typeof val === 'string') {
    return [escapeCSV(stripHtmlTags(val))];
  }

  if (val == null) {
    return [''];
  }

  if (typeof val === 'object') {
    try {
      return [escapeCSV(JSON.stringify(val) ?? '')];
    } catch {
      return [''];
    }
  }

  if (typeof val === 'function' || typeof val === 'symbol') {
    return [''];
  }

  return [escapeCSV(JSON.stringify(val))];
};

type FieldParser = (event: Event) => string[];

const parseCreatedAtField: FieldParser = (event) => [toEscapedCsv(parseCreatedAt(event.created_at))];

const parseDescriptionField: FieldParser = (event) => [toEscapedCsv(stripHtmlTags(event.description || ''))];

const parseAudienceField: FieldParser = (event) => [toEscapedCsv(joinOptionNames(event.audience))];

const parseFirmInitiativesField: FieldParser = (event) => [toEscapedCsv(joinOptionNames(event.firmInitiatives))];

const parseIndustriesField: FieldParser = (event) => [toEscapedCsv(joinOptionNames(event.industries))];

const parseDepartmentsField: FieldParser = (event) => [toEscapedCsv(joinOptionNames(event.departments))];

const parsePracticeGroupsField: FieldParser = (event) => [toEscapedCsv(joinOptionNames(event.practiceGroups))];

const parseEventDateTimeField: FieldParser = (event) => [toEscapedCsv(parseEventDateTime(event))];

const parseStartField: FieldParser = (event) => [toEscapedCsv(parseStartOrEnd(event, 'start'))];

const parseEndField: FieldParser = (event) => [toEscapedCsv(parseStartOrEnd(event, 'end'))];

const parseRecurrenceField: FieldParser = (event) => [toEscapedCsv(parseRecurrence(event.recurrence))];

const parseEventContactField: FieldParser = (event) => [
  toEscapedCsv(joinUserField(event.eventContact, 'DisplayName')),
  toEscapedCsv(joinUserField(event.eventContact, 'WorkEmail')),
];

const parseSponsoringAttorneyField: FieldParser = (event) => [
  toEscapedCsv(joinUserField(event.sponsoringAttorney, 'DisplayName')),
];

const parseMlSpeakerField: FieldParser = (event) => [toEscapedCsv(joinUserField(event.mlSpeaker, 'DisplayName'))];

const parseCreatedByUserField: FieldParser = (event) => [
  toEscapedCsv(event.created_by_user?.DisplayName ?? ''),
  toEscapedCsv(event.created_by_user?.WorkEmail ?? ''),
];

const parseUpdatedByUserField: FieldParser = (event) => [toEscapedCsv(event.updated_by_user?.DisplayName ?? '')];

const parseOutsideSpeakersField: FieldParser = (event) => [
  toEscapedCsv(joinOutsideSpeakerNames(event.outsideSpeakers)),
];

const parseEventTypeField: FieldParser = (event) => [toEscapedCsv(event.eventType?.event_type ?? '')];

const parseOfficeLocationField: FieldParser = (event) => [toEscapedCsv(joinOfficeLocationNames(event.officeLocation))];

const fieldParsers: Record<string, FieldParser> = {
  created_at: parseCreatedAtField,
  description: parseDescriptionField,
  audience: parseAudienceField,
  firmInitiatives: parseFirmInitiativesField,
  industries: parseIndustriesField,
  departments: parseDepartmentsField,
  practiceGroups: parsePracticeGroupsField,
  eventDateTime: parseEventDateTimeField,
  start: parseStartField,
  end: parseEndField,
  recurrence: parseRecurrenceField,
  eventContact: parseEventContactField,
  sponsoringAttorney: parseSponsoringAttorneyField,
  mlSpeaker: parseMlSpeakerField,
  created_by_user: parseCreatedByUserField,
  updated_by_user: parseUpdatedByUserField,
  outsideSpeakers: parseOutsideSpeakersField,
  eventType: parseEventTypeField,
  officeLocation: parseOfficeLocationField,
};

const parseField = (event: Event, key: string): string[] => {
  const parser = fieldParsers[key];
  if (parser) return parser(event);
  return parseDefaultField(event, key);
};

export const parseEvent = (value: Event) => {
  const row: string[] = [];

  for (const key in value) {
    row.push(...parseField(value, key));
  }

  return row;
};
